import { useHotkey } from '@modules/common/hooks';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Fab, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { Feedback } from 'v2/core/entities/feedback';
import { trackFeedbackEvents } from '../../utils';

interface FeedbackDrawerNavigationArrowsProps {
  onPageChange: (index: number) => void;
  feedbacks: Feedback[];
  currentFeedbackIndex: number;
  totalRecords: number;
}

const FeedbackDrawerNavigationArrows = ({
  onPageChange,
  feedbacks,
  currentFeedbackIndex,
  totalRecords,
}: FeedbackDrawerNavigationArrowsProps) => {
  const scrollToFirstHighlight = useCallback(() => {
    requestAnimationFrame(() => {
      const scrollEl = document.querySelector(
        '#expanded-feedback-container [data-highlight-id]',
      );

      if (scrollEl) {
        scrollIntoView(scrollEl, {
          behavior: 'smooth',
          block: 'start',
          boundary: document.getElementById('expanded-feedback-container'),
          skipOverflowHiddenElements: true,
        });
      }
    });
  }, []);

  const handlePagination = useCallback(
    (direction: 'previous' | 'next') => {
      let newFeedbackIndex =
        direction === 'previous'
          ? currentFeedbackIndex - 1
          : currentFeedbackIndex + 1;
      const directionLabel = direction === 'previous' ? 'Previous' : 'Next';

      if (!!feedbacks[newFeedbackIndex]) {
        onPageChange(newFeedbackIndex);

        scrollToFirstHighlight();

        trackFeedbackEvents({
          eventName: `Feedback Drawer ${directionLabel} Pagination Clicked`,
          feedbackID: feedbacks[newFeedbackIndex].id,
        });
      }
    },
    [currentFeedbackIndex, feedbacks, onPageChange, scrollToFirstHighlight],
  );

  useHotkey({
    hotkey: 'ArrowRight',
    onHotkey: () => handlePagination('next'),
    actionTitle: 'Go to next feedback record',
  });

  useHotkey({
    hotkey: 'ArrowLeft',
    onHotkey: () => handlePagination('previous'),
    actionTitle: 'Go to previous feedback record',
  });

  return (
    <Box
      sx={{
        width: '100%',
        position: 'absolute',
        top: '50%',
      }}
    >
      <Tooltip title="Use the keyboard arrows to navigate" placement="right">
        <Box component="span" sx={{ position: 'absolute', top: 0, left: '2%' }}>
          <Fab
            onClick={() => {
              handlePagination('previous');
            }}
            aria-label="Previous feedback"
            disabled={currentFeedbackIndex === 0}
            size="small"
          >
            <ChevronLeft />
          </Fab>
        </Box>
      </Tooltip>
      <Tooltip title="Use the keyboard arrows to navigate" placement="left">
        <Box
          component="span"
          sx={{ position: 'absolute', top: 0, right: '2%' }}
        >
          <Fab
            onClick={() => {
              handlePagination('next');
            }}
            aria-label="Next feedback"
            disabled={currentFeedbackIndex + 1 >= totalRecords}
            size="small"
          >
            <ChevronRight />
          </Fab>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default FeedbackDrawerNavigationArrows;
